import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "./utils";

const buttonVariants = cva(
  // font-heading (not font-medium): organic-styles.css's .btn renders in
  // Caprasimo (--font-heading) at weight 400, not the body face — the
  // class also carries font-synthesis-weight:none so it never fake-bolds.
  // No outline-none: the global *:focus-visible outline (theme.css) needs
  // to win, and `outline-none` is a real outline-style:none utility that
  // beats a @layer base rule regardless of source order.
  "font-heading !flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm transition-all disabled:pointer-events-none disabled:opacity-45 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        // Primary — solid terracotta fill, cream text. Light mode uses the
        // terracotta-700/terracotta-100 ramp step pair, not the raw
        // --primary/--primary-foreground token pair: an axe scan found
        // --primary's own ~3.03:1 fill-vs-text contrast (documented in
        // theme.css as "chrome/large-text tier, not body copy") fails AA's
        // 4.5:1 small-text floor on this text-sm/normal-weight (Caprasimo
        // ships only weight 400) label -- same precedent already established
        // by ThemeControl.tsx's active-tab fill (ThemeControl.tsx:50-62).
        // Dark mode keeps the token pair: --primary there is already
        // terracotta-400 on terracotta-900 text (~6.9:1, see ThemeControl's
        // comment), so no swap is needed. Hover/active step the ramp one/two
        // steps past base in both themes -- on a dark ground base is already
        // terracotta-400, so darker themes step LIGHTER (toward cream), not
        // darker (R3/R4 ruling: the brief's literal -600/-700 was written
        // against the light-only reference and fails contrast in dark mode).
        default:
          "bg-terracotta-700 text-terracotta-100 hover:bg-terracotta-800 active:bg-terracotta-900 dark:bg-primary dark:text-primary-foreground dark:hover:bg-terracotta-300 dark:active:bg-terracotta-200",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40",
        // Secondary — divider border, foreground-tinted hover/active fill.
        // foreground-based tints (not ink-only) work in both themes, so no
        // separate dark: override is needed (R3/R4 ruling).
        outline:
          "border border-border bg-background text-foreground hover:bg-foreground/7 active:bg-foreground/14",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        // Ghost — terracotta text, terracotta-tinted hover/active fill in
        // both themes (R3/R4 ruling: dropped the legacy dark:hover:bg-accent
        // override so the mandated tint idiom applies everywhere). Text color
        // uses the same ramp-step swap as the `default` variant's fill (see
        // its comment): raw --primary text is only ~3:1/~2.7:1 against this
        // component's text-sm label on page/card background, short of the
        // 4.5:1 small-text floor -- #367's reviewer prescribed this as the
        // systemic fix (HouseholdCard.tsx's Copy button carried the same
        // classes by hand before this variant absorbed them).
        ghost:
          "text-terracotta-700 hover:text-terracotta-800 dark:text-terracotta-300 dark:hover:text-terracotta-200 hover:bg-primary/10 active:bg-primary/18",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 px-6 has-[>svg]:px-4",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
